import { Drawer, notification } from "antd";
import cuid from "cuid";
import { find, get } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { MdOutlineAddTask } from "react-icons/md";
import Avatar from "shared/components/Avatar";
import CallButton from "shared/components/CallButton";
import Select from "shared/components/Select";
import Tabs from "shared/components/Tab";
import Tooltip from "shared/components/Tooltip";
import { useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { StoreContext } from "shared/store";
import { UPDATE_CONTACT } from "utils/api/graphql/mutations/contacts";
import { CONTACT_POINT_SYSTEM, USER_TYPES } from "utils/constants";
import CreateTaskModal from "./Modals/CreateTaskModal";
import Activities from "./Tabs/Activities";
import Information from "./Tabs/Information";
import Notes from "./Tabs/Notes";
import Projects from "./Tabs/Projects";

const View = ({
  contact,
  active,
  setActive,
  project,
  projects,
  drawer,
  onClose,
  onAssign,
  isAssigning,
  qualifiers,
  currentQualifier,
  addQualification,
  tracers,
  matchStatusesWithProcess,
}) => {
  const { state } = useLocation();
  const { user } = useContext(StoreContext);
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTACT);
  const DRAWER = t("DRAWER", { returnObjects: true });
  const TRANSLATION = t("DETAIL", { returnObjects: true });
  const qualificationsOptions = qualifiers.map(({ name, description, id }) => ({
    value: id,
    label: name,
    popover: description,
  }));

  const [updateContact] = useMutation(UPDATE_CONTACT);
  const [drawerWidth, setDrawerWidth] = useState("520px");
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(
    get(currentQualifier, "qualifier.name") || "-",
  );

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      if (windowWidth <= 768) {
        setDrawerWidth("95%");
      } else if (windowWidth <= 1024) {
        setDrawerWidth("70%");
      } else {
        setDrawerWidth("520px");
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setSelectedStatus(currentQualifier?.qualifier?.name || "-");
  }, [currentQualifier?.qualifier?.name]);

  const handleUpdateEmail = (newEmail) => {
    const existingEmailTelecom = find(contact.telecoms, {
      system: CONTACT_POINT_SYSTEM.EMAIL,
    });

    updateContact({
      variables: {
        where: { id: contact.id },
        data: {
          telecoms: [
            ...omitDeep(contact.telecoms, "__typename").filter(
              (telecom) => telecom.system !== CONTACT_POINT_SYSTEM.EMAIL,
            ),
            {
              id: get(existingEmailTelecom, "id", cuid()),
              system: CONTACT_POINT_SYSTEM.EMAIL,
              value: newEmail,
            },
          ],
        },
      },
    })
      .then(() => {
        notification.success({
          message: DRAWER.TABS.INFORMATION.UPDATE_SUCCESS,
          closeIcon: null,
        });
      })
      .catch((error) => {
        notification.error({
          message: DRAWER.TABS.INFORMATION.UPDATE_ERROR,
          closeIcon: null,
        });
      });
  };

  const handleTabChange = (key) => {
    setActive(key);
  };

  const onCloseDrawer = () => {
    onClose();
    setActive("INFORMATION");
  };

  const getPrimaryPhone = () => {
    const telecoms = get(contact, "telecoms", []);
    const phone = find(telecoms, { system: CONTACT_POINT_SYSTEM.PHONE });
    if (phone && get(phone, "value")) return get(phone, "value");
    const landline = find(telecoms, { system: CONTACT_POINT_SYSTEM.LANDLINE });
    if (landline && get(landline, "value")) return get(landline, "value");
    return null;
  };

  const handleStatusChange = (value) => {
    if (
      value === currentQualifier?.qualifier?.id &&
      selectedStatus === currentQualifier?.qualifier?.name
    )
      return;
    setSelectedStatus(value);
    addQualification({
      variables: {
        data: {
          id: cuid(),
          qualifier: { id: value },
          contact: { id: contact.id },
        },
      },
      onCompleted: () => {
        onAssign({ projectId: get(project, "id"), authorId: get(user, "id") });
      },
    });
  };

  const TABS = {
    [true]: [
      {
        key: "INFORMATION",
        children: (
          <Information
            project={project}
            translate={TRANSLATION}
            data={contact}
            onUpdateEmail={handleUpdateEmail}
          />
        ),
        label: DRAWER.TABS.INFORMATION.TITLE,
      },
    ],
    [false]: [
      {
        key: "INFORMATION",
        children: (
          <Information
            project={project}
            translate={TRANSLATION}
            data={contact}
            onUpdateEmail={handleUpdateEmail}
          />
        ),
        label: DRAWER.TABS.INFORMATION.TITLE,
      },
      {
        key: "PROJECTS",
        children: (
          <Projects
            translate={TRANSLATION}
            matchStatusesWithProcess={matchStatusesWithProcess}
            isAssigning={isAssigning}
            onAssign={onAssign}
            projects={projects}
            tracers={tracers}
          />
        ),
        label: `${DRAWER.TABS.PROJECTS.TITLE} (${projects?.count})`,
      },
      {
        key: "ACTIVITIES",
        children: (
          <Activities
            project={project}
            contactId={contact?.id}
            phoneNumber={getPrimaryPhone()}
          />
        ),
        label: DRAWER.TABS.ACTIVITIES.TITLE,
      },
      {
        key: "NOTES",
        children: <Notes projectId={project?.id} />,
        label: DRAWER.TABS.NOTES.TITLE,
      },
    ],
  };

  return (
    <Drawer
      title={<></>}
      placement="right"
      onClose={onCloseDrawer}
      open={drawer}
      className="contact__drawer"
      contentWrapperStyle={{
        width: drawerWidth,
      }}
    >
      <div className="profile--avatar">
        <Avatar
          mode={"text"}
          size={"large"}
          value={`${get(contact, "user.firstname", "")?.substring(0, 1)}${get(
            contact,
            "user.lastname",
            "",
          )?.substring(0, 1)}`}
        />
        <div className="info">
          <span className="name">
            {get(contact, "user.firstname")} {get(contact, "user.lastname")}
          </span>
          <span className="source">{get(project, "tracer.name", " ")}</span>
        </div>
        <Select
          popupMatchSelectWidth={false}
          type="tag"
          value={selectedStatus}
          onChange={handleStatusChange}
          options={qualificationsOptions}
        />
      </div>
      <div className="cta-bar">
        <Tooltip
          title={DRAWER.CREATE_TASK?.TOOLTIP}
          getPopupContainer={(trigger) =>
            trigger?.closest(".ant-drawer-content") || document.body
          }
        >
          <div className="cta-bar__btn" onClick={() => setTaskModalOpen(true)}>
            <MdOutlineAddTask />
          </div>
        </Tooltip>
        <CallButton
          phoneNumber={getPrimaryPhone()}
          contactId={contact?.id}
          projectId={project?.id}
          getPopupContainer={(trigger) =>
            trigger?.closest(".ant-drawer-content") || document.body
          }
        />
      </div>
      <Tabs
        items={get(
          TABS,
          get(user, "meta.type") === USER_TYPES.EXTERNAL_ADVISOR.type,
          [],
        )}
        activeKey={active}
        onChange={handleTabChange}
      />
      <CreateTaskModal
        open={taskModalOpen}
        onClose={() => setTaskModalOpen(false)}
        contact={contact}
        project={project}
        user={user}
        t={t}
      />
    </Drawer>
  );
};
export default View;
